import {
  HaulActionIdentifierType,
  type HaulAction,
  type HaulActionIdentifier,
} from "@amzn/yt-yard-console-service-typescript-client/com/amazon/ytyardconsoleservice";

import type {
  ContentMetadata,
  HaulIdentifier,
  VisitReason,
  YardVisit,
} from "@/features/relayYardControl/types/visits";
import type { YardAlert } from "@/features/relayYardControl/types/alert";
import type {
  LocationData,
  YardDetectionDataState,
} from "@/features/relayYardControl/store/yardDetectionData/slice";

/** API response shape from updateEquipmentOtherInfo */
export interface OtherInfoApiResponse {
  equipment?: {
    operator?: string;
    ownerCode?: string;
    equipmentEmptyStatus?: string;
    contentMetadata?: ContentMetadata;
    activeVisitReasons?: HaulAction[];
    actualEquipmentSeals?: Array<{ number?: string; source?: string; condition?: string }>;
    expectedEquipmentSeals?: Array<{ number?: string; source?: string; condition?: string }>;
  };
}

/** API response shape from updateEquipmentCoreInfo */
export interface CoreInfoApiResponse {
  equipment?: {
    equipmentType?: string;
    equipmentNumber?: string | null;
    licensePlateNumber?: string;
    countryCode?: string;
    subnationalEntity?: string;
    ownerCode?: string;
  };
}

/** Combined API response from trailerSaveHandler */
export interface SaveApiResponse {
  coreInfoResponse?: CoreInfoApiResponse;
  otherInfoResponse?: OtherInfoApiResponse;
  operationalCodes?: string[];
}

/**
 * Find a visit by equipment ID in a location.
 * Returns null if visit not found.
 */
export const findVisitByEquipmentId = (location: LocationData, sectionId: string) => {
  const visit = location.visits?.find(
    (v) => v.data?.equipment?.equipmentDetails?.identifier?.id === sectionId
  );
  return visit?.data?.equipment ? visit : null;
};

/**
 * Apply common equipment field changes to visit data.
 * Maps UI field names to their paths in the visit data structure.
 */
export const applyEquipmentFieldChanges = (
  visit: YardVisit,
  flatChanges: Record<string, unknown>
) => {
  const equipmentDetails = visit.data?.equipment?.equipmentDetails;
  if (!equipmentDetails) return;

  if (flatChanges.licensePlate !== undefined) {
    if (!equipmentDetails.licensePlate) {
      // For equipments that never had a LP object; ensures value isn't dropped
      equipmentDetails.licensePlate = {};
    }
    equipmentDetails.licensePlate.licensePlateNumber = String(flatChanges.licensePlate);
  }
  if (flatChanges.ownerCode !== undefined) {
    equipmentDetails.ownerCode = String(flatChanges.ownerCode);
  }
  if (flatChanges.type !== undefined) {
    equipmentDetails.type = String(flatChanges.type);
  }
  if (flatChanges.trailerNumber !== undefined) {
    equipmentDetails.equipmentNumber = String(flatChanges.trailerNumber);
  }
};

const toVisitHaulIdentifier = (identifiers: HaulActionIdentifier[]): HaulIdentifier => ({
  isaId: identifiers.find((identifier) => identifier.type === HaulActionIdentifierType.ISA)?.id,
  vrHaulIdentifiers: {
    vrId: identifiers.find((identifier) => identifier.type === HaulActionIdentifierType.VR_ID)?.id,
    locationPlanId: identifiers.find(
      (identifier) => identifier.type === HaulActionIdentifierType.LOCATION_PLAN
    )?.id,
  },
});

/** Apply updateEquipmentOtherInfo response — only other-info fields */
export const applyOtherInfoResponse = (visit: YardVisit, response: OtherInfoApiResponse) => {
  const resp = response.equipment;
  if (!resp) return;
  const equipment = visit.data?.equipment;
  if (!equipment) return;

  if (resp.operator !== undefined) {
    equipment.operator = resp.operator;
    if (equipment.equipmentCarrierDetails) {
      equipment.equipmentCarrierDetails.scac = resp.operator;
    }
  }
  if (resp.ownerCode !== undefined && equipment.equipmentDetails) {
    equipment.equipmentDetails.ownerCode = resp.ownerCode;
  }
  if (resp.equipmentEmptyStatus !== undefined) {
    equipment.equipmentEmptyStatus = resp.equipmentEmptyStatus;
  }
  if (resp.contentMetadata !== undefined) {
    equipment.contentMetadata = resp.contentMetadata;
  }
  if (
    equipment.visitDetails &&
    Array.isArray(resp.activeVisitReasons) &&
    resp.activeVisitReasons.every((reason) => Array.isArray(reason.haulIdentifiers))
  ) {
    const currentReasons = equipment.visitDetails.activeVisitReasons || [];
    equipment.visitDetails.activeVisitReasons = resp.activeVisitReasons.map((reason) => {
      const haulIdentifier = toVisitHaulIdentifier(reason.haulIdentifiers || []);
      const currentReason = currentReasons.find(
        ({ haulIdentifier: current }) =>
          current?.isaId === haulIdentifier.isaId &&
          current?.vrHaulIdentifiers?.vrId === haulIdentifier.vrHaulIdentifiers?.vrId &&
          current?.vrHaulIdentifiers?.locationPlanId ===
            haulIdentifier.vrHaulIdentifiers?.locationPlanId
      );
      // YardConsole projects identifiers; DAS supplies the remaining reason fields.
      return { ...currentReason, haulIdentifier } as VisitReason;
    });
  }
  if (resp.actualEquipmentSeals) {
    equipment.actualSeals = resp.actualEquipmentSeals.map((s) => ({
      number: s.number || "",
      source: s.source || "",
      condition: s.condition || "",
    }));
  }
  if (resp.expectedEquipmentSeals) {
    equipment.expectedSeals = resp.expectedEquipmentSeals.map((s) => ({
      number: s.number || "",
      source: s.source || "",
      condition: s.condition || "",
    }));
  }
};

/** Apply updateEquipmentCoreInfo response — only core-info fields */
export const applyCoreInfoResponse = (visit: YardVisit, response: CoreInfoApiResponse) => {
  const resp = response.equipment;
  if (!resp) return;
  const details = visit.data?.equipment?.equipmentDetails;
  if (!details) return;

  if (resp.equipmentType !== undefined) {
    details.type = resp.equipmentType;
  }
  if (resp.equipmentNumber !== undefined) {
    details.equipmentNumber = resp.equipmentNumber || undefined;
  }
  if (resp.ownerCode !== undefined) {
    details.ownerCode = resp.ownerCode;
  }
  // For equipments that never had a LP object; ensures value isn't dropped
  const hasLpResponse =
    resp.licensePlateNumber !== undefined ||
    resp.countryCode !== undefined ||
    resp.subnationalEntity !== undefined;
  if (hasLpResponse && !details.licensePlate) {
    details.licensePlate = {};
  }
  if (resp.licensePlateNumber !== undefined && details.licensePlate) {
    details.licensePlate.licensePlateNumber = resp.licensePlateNumber;
  }
  if (resp.countryCode !== undefined && details.licensePlate) {
    details.licensePlate.countryCode = resp.countryCode;
  }
  if (resp.subnationalEntity !== undefined && details.licensePlate) {
    details.licensePlate.subnationalEntity = resp.subnationalEntity;
  }
};

/** Apply operational codes update to visit data */
export const applyOperationalCodesUpdate = (visit: YardVisit, operationalCodes: string[]) => {
  const equipment = visit.data?.equipment;
  if (!equipment) return;
  equipment.operationalCodes = operationalCodes;
};

/** Apply available API responses to visit data */
export const applySaveApiResponses = (visit: YardVisit, apiResponse: SaveApiResponse) => {
  if (apiResponse.coreInfoResponse) {
    applyCoreInfoResponse(visit, apiResponse.coreInfoResponse);
  }
  if (apiResponse.otherInfoResponse) {
    applyOtherInfoResponse(visit, apiResponse.otherInfoResponse);
  }
  if (apiResponse.operationalCodes) {
    applyOperationalCodesUpdate(visit, apiResponse.operationalCodes);
  }
};

/**
 * Update an alert across all locations in state.
 * Shared by completeAlert and dismissAlert reducers.
 */
export const updateAlertInState = (
  state: YardDetectionDataState,
  updatedAlert: { yardAlertId?: string }
) => {
  const yardAlertId = updatedAlert.yardAlertId;
  if (!yardAlertId) return;

  Object.values(state.data).forEach((locationData) => {
    if (locationData.alerts) {
      const alertIndex = locationData.alerts.findIndex(
        (alert) => alert.data?.yardAlertId === yardAlertId
      );
      if (alertIndex !== -1) {
        locationData.alerts[alertIndex] = updatedAlert as YardAlert;
      }
    }
  });
};
